import { normalizeHttpHost, normalizeHttpMethod } from '@terra/shared-utils';
import {
  getHarOperationKey,
  getHarUrlHost,
  type HarCreateGroup,
  type HarReassociateRow,
} from './har-reassociate-identity';

export type PlannedHarCreate = {
  harEntryId: string;
  path: string;
  method: string;
  host: string;
  operation: string | undefined;
};

export type ExistingCreateCollisionEndpoint = {
  id: string;
  method: string;
  host: string;
  path: string;
  graphqlOperationName?: string | null;
  soapOperationName?: string | null;
  xmlOperationName?: string | null;
  mcpOperationName?: string | null;
  grpcServiceName?: string | null;
  grpcMethodName?: string | null;
};

export const plannedCreateIdentityKey = (item: PlannedHarCreate): string =>
  [
    normalizeHttpMethod(item.method),
    normalizeHttpHost(item.host),
    item.path.trim(),
    item.operation ?? '',
  ].join('\0');

export const existingCreateIdentityKey = (
  endpoint: ExistingCreateCollisionEndpoint,
): string =>
  [
    normalizeHttpMethod(endpoint.method),
    normalizeHttpHost(endpoint.host),
    endpoint.path,
    endpoint.graphqlOperationName ??
      endpoint.soapOperationName ??
      endpoint.xmlOperationName ??
      endpoint.mcpOperationName ??
      (endpoint.grpcServiceName && endpoint.grpcMethodName
        ? `${endpoint.grpcServiceName}/${endpoint.grpcMethodName}`
        : ''),
  ].join('\0');

export const toPlannedHarCreate = ({
  row,
  path,
}: {
  row: HarReassociateRow;
  path: string;
}): PlannedHarCreate => ({
  harEntryId: row.id,
  path,
  method: row.method,
  host: getHarUrlHost(row.url),
  operation: getHarOperationKey(row),
});

export const uniquePlannedCreates = (
  planned: readonly PlannedHarCreate[],
): PlannedHarCreate[] => {
  const seen = new Set<string>();

  return planned.filter((item) => {
    const key = plannedCreateIdentityKey(item);

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);

    return true;
  });
};

export const findCreateCollision = ({
  planned,
  existing,
  sourceEndpointId,
}: {
  planned: readonly PlannedHarCreate[];
  existing: readonly ExistingCreateCollisionEndpoint[];
  sourceEndpointId: string;
}): 'existing' | undefined => {
  const plannedKeys = uniquePlannedCreates(planned).map(plannedCreateIdentityKey);
  const existingKeys = new Set(
    existing
      .filter((endpoint) => endpoint.id !== sourceEndpointId)
      .map(existingCreateIdentityKey),
  );

  if (plannedKeys.some((key) => existingKeys.has(key))) {
    return 'existing';
  }

  return undefined;
};

export const toCreateFromHarsTarget = ({
  groups,
  pathsByGroupId,
}: {
  groups: readonly HarCreateGroup[];
  pathsByGroupId: Record<string, string>;
}):
  | { kind: 'createFromHars'; path: string }
  | {
      kind: 'createFromHars';
      items: Array<{ harEntryId: string; path: string }>;
    } => {
  const planned = groups.flatMap((group) => {
    const first = group.rows[0];
    const path = (pathsByGroupId[group.id] ?? '').trim();

    if (!first) {
      return [];
    }

    return [
      toPlannedHarCreate({
        row: first,
        path,
      }),
    ];
  });
  const unique = uniquePlannedCreates(planned);
  const firstPath = unique[0]?.path;

  if (unique.length === 1 && firstPath) {
    return { kind: 'createFromHars', path: firstPath };
  }

  return {
    kind: 'createFromHars',
    items: groups.flatMap((group) => {
      const path = (pathsByGroupId[group.id] ?? '').trim();

      return group.rows.map((row) => ({
        harEntryId: row.id,
        path,
      }));
    }),
  };
};
